import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { USER_API } from "config";
import { budgetKeys } from "queries";
import {
  benchmarkSetFromCatalogRows,
  getBenchmarkSet,
  normalizeProductionType,
  type BenchmarkSet,
  type BenchmarkSource,
} from "components/dashboard/body/projects/panels/budget/budgetBenchmarks";
import type {
  BudgetBenchmarkProfileRow,
  BudgetBenchmarkQueryResponse,
} from "types/budget-benchmark";
import { useAxios } from "../useAxios";

const ONE_HOUR_MS = 60 * 60 * 1000;

interface UseBudgetBenchmarksParams {
  productionType?: string | null;
  enabled?: boolean;
  /** The source the user picked. Peers only apply while peer benchmarks are available. */
  selectedSource?: BenchmarkSource;
}

function rowsForProductionType(
  rows: BudgetBenchmarkProfileRow[],
  productionType: string
): BudgetBenchmarkProfileRow[] {
  const normalized = normalizeProductionType(productionType);
  return rows.filter((row) => row.production_type === normalized);
}

function catalogRowsToBenchmarkSet(rows: BudgetBenchmarkProfileRow[]): BenchmarkSet | null {
  return benchmarkSetFromCatalogRows(
    rows.map((row) => ({
      bucket: row.bucket,
      min_pct: row.min_pct,
      typical_pct: row.typical_pct,
      max_pct: row.max_pct,
    }))
  );
}

export const useBudgetBenchmarks = ({
  productionType,
  enabled = true,
  selectedSource = "industry",
}: UseBudgetBenchmarksParams = {}) => {
  const axios = useAxios({ allResponse: false });
  const normalizedProductionType = normalizeProductionType(productionType);

  const query = useQuery({
    queryKey: budgetKeys.budgetBenchmarks(normalizedProductionType),
    queryFn: async (): Promise<BudgetBenchmarkQueryResponse> => {
      const response = await axios.get<{ data: BudgetBenchmarkQueryResponse }>(
        USER_API.getBudgetBenchmarksForProductionType(normalizedProductionType)
      );
      const payload = response as unknown;
      if (payload && typeof payload === "object" && "industry" in payload) {
        return payload as BudgetBenchmarkQueryResponse;
      }
      if (payload && typeof payload === "object" && "data" in payload) {
        return (payload as { data: BudgetBenchmarkQueryResponse }).data;
      }
      throw new Error("Invalid budget benchmark response");
    },
    enabled: enabled && Boolean(productionType),
    staleTime: ONE_HOUR_MS,
    refetchOnWindowFocus: false,
  });

  const industryBenchmarkSet = useMemo(() => {
    const rows = query.data?.industry.profiles ?? [];
    const scopedRows = rows.length > 0 ? rowsForProductionType(rows, normalizedProductionType) : [];
    return (
      catalogRowsToBenchmarkSet(scopedRows) ?? getBenchmarkSet(normalizedProductionType, "industry")
    );
  }, [normalizedProductionType, query.data?.industry.profiles]);

  const peerBenchmarkSet = useMemo(() => {
    const peerRows = query.data?.peers?.buckets ?? [];
    if (!query.data?.peers?.available || peerRows.length === 0) return null;
    return catalogRowsToBenchmarkSet(peerRows);
  }, [query.data?.peers]);

  // A refetch can drop peers (too few samples) after the user picked them. The comparison then
  // uses industry values, so it must also be labeled industry; the stored pick is ignored until
  // peers come back.
  const benchmarkSource: BenchmarkSource =
    selectedSource === "peers" && peerBenchmarkSet ? "peers" : "industry";
  const activeBenchmarkSet: BenchmarkSet =
    benchmarkSource === "peers" && peerBenchmarkSet ? peerBenchmarkSet : industryBenchmarkSet;

  return {
    industryBenchmarkSet,
    peerBenchmarkSet,
    peerSampleSize: query.data?.peers?.sample_size ?? null,
    peerBenchmarkAvailable: Boolean(query.data?.peers?.available && peerBenchmarkSet),
    /** The source the comparison actually uses; industry whenever peers are unavailable. */
    benchmarkSource,
    activeBenchmarkSet,
    catalogVersion: query.data?.industry.version ?? 1,
    isLoading: query.isLoading,
    isError: query.isError,
    usingFallback: query.isError || !query.data,
    refetch: query.refetch,
  };
};
